import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

/**
 * /v/:option — one-tap vote shortlink.
 *
 *   Use case: paste this URL into an Instagram/TikTok reel caption or
 *   bio. The viewer taps it → vote registers anonymously → they're
 *   redirected to /browse with the matching filter applied — under 1
 *   second, no buttons to press, no "are you sure?" friction.
 *
 *   The page renders for ~600 ms purely so the viewer SEES which option
 *   was registered before the browse view loads. Without the breather
 *   it would feel like a broken link rather than a delightful "boom,
 *   you're in" moment.
 *
 *   How it picks the poll: looks up TODAY's poll, finds the option whose
 *   `key` matches `:option`, registers the vote there, and routes using
 *   the poll's `filter_param`. So `/v/tent` votes on whichever poll is
 *   live today that has a `tent` option (camping-style + tonight-outfit).
 *   If no option matches today's poll, we fall back to /vote so the user
 *   isn't stranded.
 */
export default function VoteOne() {
  const { option } = useParams();
  const navigate = useNavigate();
  const [poll, setPoll] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: t } = await api.get("/polls/today");
        if (cancelled) return;
        const opt = (t.options || []).find((o) => o.key === option);
        if (!opt) {
          // Try every poll until we find a match (so /v/scotland still
          // works on a day where the regional poll isn't the active one).
          const POLL_IDS = ["weekend-region","dealbreakers","camping-style",
            "when-escaping","tonight-outfit","dog-policy","why-camp"];
          for (const pid of POLL_IDS) {
            if (pid === t.poll_id) continue;
            try {
              const { data: p } = await api.get(`/polls/${pid}`);
              const o = (p.options || []).find((x) => x.key === option);
              if (o) {
                await api.post(`/polls/${pid}/vote`, { option: option });
                setPoll({ ...p, _votedOpt: o });
                setTimeout(() => {
                  navigate(`/browse?${encodeURIComponent(p.filter_param || "region")}=${encodeURIComponent(o.browse_filter)}`, { replace: true });
                }, 700);
                return;
              }
            } catch { /* try next poll */ }
          }
          setError("That option isn't in any poll. Try the full vote page instead.");
          return;
        }
        await api.post(`/polls/${t.poll_id}/vote`, { option: option });
        window.dispatchEvent(new CustomEvent("cc:presence-bump", { detail: { source: "shortlink" } }));
        setPoll({ ...t, _votedOpt: opt });
        setTimeout(() => {
          navigate(`/browse?${encodeURIComponent(t.filter_param || "region")}=${encodeURIComponent(opt.browse_filter)}`, { replace: true });
        }, 700);
      } catch {
        if (!cancelled) setError("Couldn't reach the server. Try once more?");
      }
    })();
    return () => { cancelled = true; };
  }, [option, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0e0e0e] text-white p-6 text-center" data-testid="vote-one-error">
        <p className="font-display text-2xl mb-2">Oh no — bad link</p>
        <p className="text-sm text-[#bbb] mb-6">{error}</p>
        <Link to="/vote" className="btn-primary inline-flex items-center gap-2">Go to today's poll <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center text-white p-6"
      style={{ background: "linear-gradient(160deg,#1e3a8a 0%,#7c3aed 35%,#ec4899 70%,#f97316 100%)" }}
      data-testid="vote-one-loading"
    >
      <Loader2 className="w-12 h-12 animate-spin mb-6" strokeWidth={1.5} />
      {poll && poll._votedOpt ? (
        <>
          <div className="text-[10px] mono uppercase tracking-[0.3em] text-white/80 mb-2">vote registered ✓</div>
          <div className="font-display text-4xl sm:text-5xl text-center mb-2 drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)]">
            {poll._votedOpt.label}
          </div>
          <div className="text-sm text-white/90">Loading {poll._votedOpt.label} campsites…</div>
        </>
      ) : (
        <div className="font-display text-2xl">Counting your vote…</div>
      )}
    </div>
  );
}
